import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { WalletLog } from '../../../apiTypes';
import Loader from '../../../components/Loader';
import TransactionCategories from '../../../constants/transactionCategories';
import { t } from '../../../constants/translations';
import { useLocale } from '../../../contexts/locale.context';
import { TimeScale } from '../Reports';
import { useGetArchivedWalletLogs } from './useGetArchivedWalletLogs';
import WalletsChart from './WalletsChart';
import WalletsPie from './WalletsPie';

export type ReportItem = {
  id: string;
  ingredientName: string;
  added: number;
  sold: number;
  production: number;
  moved: number;
  inventored: number;
};

type Props = {
  timeRange: [number, number];
  timeScale: TimeScale;
};

export const logCategoryMap: Record<WalletLog['type'], keyof typeof t> = {
  transfer: TransactionCategories.transfer,
  supply: TransactionCategories.supply,
  shift: TransactionCategories.salary,
  order: TransactionCategories.in_house_sales,
  custom: 'unknown',
  cancel: 'unknown',
  cancelOrder: 'unknown',
};

const ReportByWallets = ({ timeRange, timeScale }: Props) => {
  const { lang } = useLocale();

  const { logs, isLoading } = useGetArchivedWalletLogs(timeRange);

  const filteredLogs = useMemo(
    () => logs.filter((log) => !log.canceled && log.type !== 'cancel' && log.type !== 'transfer'),
    [logs]
  );

  if (isLoading) {
    return (
      <Box sx={{ mt: 10 }}>
        <Loader open />
      </Box>
    );
  }

  if (filteredLogs.length === 0) {
    return (
      <Box sx={{ mt: 10 }}>
        <Typography textAlign="center" color={(t) => t.palette.text.disabled}>
          {t.there_is_nothing[lang]}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <WalletsPie logs={filteredLogs} />
      <WalletsChart timeRange={timeRange} timeScale={timeScale} logs={filteredLogs} />
    </>
  );
};

export default ReportByWallets;
